import React, { Fragment, useMemo, useState } from "react";
import "./Styles.css";
import { useWindowWidth } from "@react-hook/window-size";
import Footer from "../../Universal/Footer/Index";
import HeaderToMainPage from "../../Universal/Header/Index";
import JumbotronHero from "../../Universal/HeroJumbo/Index";

const INITIAL_FORM = {
  fullName: "",
  companyName: "",
  email: "",
  telegram: "",
  website: "",
  companyType: "",
  jurisdiction: "",
  operatorStage: "",
  deploymentTimeline: "",
  monthlyVolume: "",
  licensingStatus: "",
  monetizationModel: [],
  documentationNeeds: [],
  technicalAreas: [],
  useCase: "",
  currentStack: "",
  integrationNeeds: "",
  teamStructure: "",
  fundingContext: "",
  securityExpectations: "",
  additionalNotes: "",
  ndaRequired: false,
  wantsDemo: true,
  agreesReview: false
};

const COMPANY_TYPES = [
  "Existing casino operator",
  "Startup operator / founder",
  "Sweepstakes operator",
  "White-label aggregator",
  "Gaming studio / supplier",
  "Affiliate / media group exploring ownership",
  "Investor / acquisition evaluator",
  "Other"
];

const OPERATOR_STAGES = [
  "Researching infrastructure options",
  "Actively evaluating vendors",
  "Preparing to launch",
  "Replacing current stack",
  "Expanding to multi-brand / multi-tenant",
  "Acquisition / partnership review"
];

const DEPLOYMENT_TIMELINES = [
  "Immediately",
  "Within 30 days",
  "Within 60–90 days",
  "Within 3–6 months",
  "Exploratory / no fixed date yet"
];

const MONTHLY_VOLUME = [
  "Pre-launch / not live yet",
  "Under $50k monthly volume",
  "$50k–$250k monthly volume",
  "$250k–$1M monthly volume",
  "$1M+ monthly volume",
  "Prefer not to disclose"
];

const LICENSING_STATUS = [
  "Licensed and active",
  "License in progress",
  "Working with legal/compliance now",
  "Sweepstakes / promotional model",
  "Undecided / exploring structure"
];

const MONETIZATION_OPTIONS = [
  "Revenue share",
  "Flat monthly platform fee",
  "Hybrid fee structure",
  "Onboarding / setup fee",
  "Custom operator licensing agreement"
];

const DOC_NEEDS = [
  "Platform overview",
  "Technical architecture",
  "Multi-tenant system docs",
  "Provably fair / verification docs",
  "Operator lifecycle documentation",
  "API / integration reference",
  "Deployment / infrastructure overview",
  "Game catalog & capabilities",
  "Security / audit posture summary",
  "Commercial / licensing materials"
];

const TECH_AREAS = [
  "Tenant provisioning",
  "Authentication / session model",
  "Wallet / ledger system",
  "Provably fair lifecycle",
  "Replay / dispute tooling",
  "Admin controls",
  "Compliance / risk controls",
  "API integration",
  "Deployment architecture",
  "White-label branding system"
];

const CheckboxGroup = ({ items, selected, onChange, name }) => {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(name, selected.filter((item) => item !== value));
    } else {
      onChange(name, [...selected, value]);
    }
  };

  return (
    <div className="rd-check-grid">
      {items.map((item) => {
        const active = selected.includes(item);
        return (
          <button
            key={item}
            type="button"
            className={`rd-check-card ${active ? "is-active" : ""}`}
            onClick={() => toggle(item)}
          >
            <span className="rd-check-indicator" aria-hidden="true">
              {active ? "✓" : ""}
            </span>
            <span className="rd-check-label">{item}</span>
          </button>
        );
      })}
    </div>
  );
}

const RequestDocumentation = () => {
  const width = useWindowWidth();
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);

  const qualificationSignals = useMemo(
    () => [
      "Official operator-grade materials shared selectively",
      "Technical docs prioritized for qualified infrastructure discussions",
      "Architecture, fairness, and deployment docs available by request",
      "Commercial and technical review can be paired with live walkthrough",
      "Sensitive implementation details are not exposed publicly"
    ],
    []
  );

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    try {
        const response = await fetch("https://formspree.io/f/xeevregr", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
        },
        body: JSON.stringify({
            ...form,
            monetizationModel: form.monetizationModel.join(", "),
            documentationNeeds: form.documentationNeeds.join(", "),
            technicalAreas: form.technicalAreas.join(", ")
        })
        });

        if (response.ok) {
        setSubmitted(true);
        } else {
        console.error("Form submission failed");
        }
    } catch (err) {
        console.error(err);
    }
    };

  return (
    <Fragment>
      <HeaderToMainPage />
      {width <= 775 ? <div style={{ height: 87.25 }} /> : null}

      <JumbotronHero
        kicker="OPERATOR ACCESS • CONTROLLED DOCUMENTATION REQUEST"
        title={
          <>
            Request <span className="jh-accent">Official Documentation</span> for{" "}
            <span className="jh-accent2">Ethosino Infrastructure</span>
          </>
        }
        subtitle="Qualified operators, infrastructure evaluators, and serious launch partners can request controlled access to platform documentation, architecture summaries, technical materials, and operational review assets."
        primaryCta={{ label: "Submit Documentation Request", href: "#request-docs-form" }}
        secondaryCta={{ label: "Book a Live Demo", href: "/book-demo" }}
        checklistTitle="Available Upon Review"
        checklist={qualificationSignals}
        bgImage="/assets/images/hero/shake.png"
      />

      <main className="rd-page">
        <div className="rd-bg" aria-hidden="true">
          <div className="rd-orb rd-orb--a" />
          <div className="rd-orb rd-orb--b" />
          <div className="rd-orb rd-orb--c" />
          <div className="rd-grid" />
          <div className="rd-noise" />
        </div>

        <section className="rd-hero-strip">
          <div className="rd-container">
            <div className="rd-hero-strip-inner">
              <div className="rd-strip-copy">
                <div className="rd-kicker">CONTROLLED ACCESS MODEL</div>
                <h2 className="rd-strip-title">
                  Public overview first. <span>Deep technical materials selectively shared.</span>
                </h2>
                <p className="rd-strip-text">
                  Ethosino’s documentation is structured to support real operator conversations.
                  Public-facing materials explain the platform clearly, while deeper technical
                  documentation, architecture artifacts, and internal review materials are shared
                  case-by-case with qualified parties.
                </p>
              </div>

              <div className="rd-strip-metrics">
                <div className="rd-mini-panel">
                  <div className="rd-mini-label">Access posture</div>
                  <div className="rd-mini-value">Qualified review only</div>
                </div>
                <div className="rd-mini-panel">
                  <div className="rd-mini-label">Best for</div>
                  <div className="rd-mini-value">Operators, partners, acquirers</div>
                </div>
                <div className="rd-mini-panel">
                  <div className="rd-mini-label">Optional pairing</div>
                  <div className="rd-mini-value">Live demo + guided walkthrough</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rd-section">
          <div className="rd-container">
            <div className="rd-layout">
              <div className="rd-main">
                <div className="rd-section-head">
                  <div className="rd-kicker">REQUEST FORM</div>
                  <h2 className="rd-h2" id="request-docs-form">
                    Operator Documentation Access Request
                  </h2>
                  <p className="rd-lede">
                    Use this form to request official Ethosino documentation for technical,
                    operational, or commercial evaluation. Requests with better context receive
                    better tailored material.
                  </p>
                </div>

                <form className="rd-form-shell" onSubmit={onSubmit}>
                  <section className="rd-form-section">
                    <div className="rd-form-section-head">
                      <div className="rd-form-step">01</div>
                      <div>
                        <h3 className="rd-h3">Primary Contact</h3>
                        <p className="rd-muted">
                          Identify who is requesting access and how follow-up should happen.
                        </p>
                      </div>
                    </div>

                    <div className="rd-form-grid rd-form-grid--2">
                      <label className="rd-field">
                        <span className="rd-label">Full name</span>
                        <input
                          className="rd-input"
                          value={form.fullName}
                          onChange={(e) => handleChange("fullName", e.target.value)}
                          placeholder="Jane Smith"
                          required
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Company / group name</span>
                        <input
                          className="rd-input"
                          value={form.companyName}
                          onChange={(e) => handleChange("companyName", e.target.value)}
                          placeholder="Acme Gaming Group"
                          required
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Work email</span>
                        <input
                          className="rd-input"
                          type="email"
                          value={form.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="founder@company.com"
                          required
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Telegram / direct contact</span>
                        <input
                          className="rd-input"
                          value={form.telegram}
                          onChange={(e) => handleChange("telegram", e.target.value)}
                          placeholder="@username or contact handle"
                        />
                      </label>

                      <label className="rd-field rd-field--full">
                        <span className="rd-label">Website / current brand URL</span>
                        <input
                          className="rd-input"
                          value={form.website}
                          onChange={(e) => handleChange("website", e.target.value)}
                          placeholder="https://yourbrand.com"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="rd-form-section">
                    <div className="rd-form-section-head">
                      <div className="rd-form-step">02</div>
                      <div>
                        <h3 className="rd-h3">Operator Profile</h3>
                        <p className="rd-muted">
                          Help classify the request so the right documentation bundle is sent.
                        </p>
                      </div>
                    </div>

                    <div className="rd-form-grid rd-form-grid--2">
                      <label className="rd-field">
                        <span className="rd-label">Company type</span>
                        <select
                          className="rd-input rd-select"
                          value={form.companyType}
                          onChange={(e) => handleChange("companyType", e.target.value)}
                          required
                        >
                          <option value="">Select company type</option>
                          {COMPANY_TYPES.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Jurisdiction / target market</span>
                        <input
                          className="rd-input"
                          value={form.jurisdiction}
                          onChange={(e) => handleChange("jurisdiction", e.target.value)}
                          placeholder="e.g. Curacao, international, sweepstakes, U.S. promo model"
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Operator stage</span>
                        <select
                          className="rd-input rd-select"
                          value={form.operatorStage}
                          onChange={(e) => handleChange("operatorStage", e.target.value)}
                          required
                        >
                          <option value="">Select stage</option>
                          {OPERATOR_STAGES.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Desired deployment timeline</span>
                        <select
                          className="rd-input rd-select"
                          value={form.deploymentTimeline}
                          onChange={(e) => handleChange("deploymentTimeline", e.target.value)}
                        >
                          <option value="">Select timeline</option>
                          {DEPLOYMENT_TIMELINES.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Current or expected monthly volume</span>
                        <select
                          className="rd-input rd-select"
                          value={form.monthlyVolume}
                          onChange={(e) => handleChange("monthlyVolume", e.target.value)}
                        >
                          <option value="">Select range</option>
                          {MONTHLY_VOLUME.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Licensing / compliance status</span>
                        <select
                          className="rd-input rd-select"
                          value={form.licensingStatus}
                          onChange={(e) => handleChange("licensingStatus", e.target.value)}
                        >
                          <option value="">Select status</option>
                          {LICENSING_STATUS.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </section>

                  <section className="rd-form-section">
                    <div className="rd-form-section-head">
                      <div className="rd-form-step">03</div>
                      <div>
                        <h3 className="rd-h3">Commercial Interest</h3>
                        <p className="rd-muted">
                          Clarify how you are evaluating Ethosino from an operator or infrastructure perspective.
                        </p>
                      </div>
                    </div>

                    <label className="rd-field">
                      <span className="rd-label">Monetization / commercial model of interest</span>
                    </label>
                    <CheckboxGroup
                      items={MONETIZATION_OPTIONS}
                      selected={form.monetizationModel}
                      onChange={handleChange}
                      name="monetizationModel"
                    />

                    <div className="rd-form-grid rd-form-grid--2 rd-mt-16">
                      <label className="rd-field">
                        <span className="rd-label">Team structure</span>
                        <textarea
                          className="rd-input rd-textarea rd-textarea--sm"
                          value={form.teamStructure}
                          onChange={(e) => handleChange("teamStructure", e.target.value)}
                          placeholder="Who would evaluate, launch, operate, or integrate the platform on your side?"
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Funding / acquisition / budget context</span>
                        <textarea
                          className="rd-input rd-textarea rd-textarea--sm"
                          value={form.fundingContext}
                          onChange={(e) => handleChange("fundingContext", e.target.value)}
                          placeholder="Optional: are you bootstrapped, funded, replacing an existing stack, or evaluating for acquisition?"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="rd-form-section">
                    <div className="rd-form-section-head">
                      <div className="rd-form-step">04</div>
                      <div>
                        <h3 className="rd-h3">Documentation Requested</h3>
                        <p className="rd-muted">
                          Select the categories of documentation you want reviewed.
                        </p>
                      </div>
                    </div>

                    <label className="rd-field">
                      <span className="rd-label" style={{ marginBottom: 15 }}>Documentation categories</span>
                    </label>
                    <CheckboxGroup
                      items={DOC_NEEDS}
                      selected={form.documentationNeeds}
                      onChange={handleChange}
                      name="documentationNeeds"
                    />

                    <label className="rd-field rd-mt-18">
                      <span className="rd-label" style={{ marginBottom: 15 }}>Technical areas of greatest interest</span>
                    </label>
                    <CheckboxGroup
                      items={TECH_AREAS}
                      selected={form.technicalAreas}
                      onChange={handleChange}
                      name="technicalAreas"
                    />
                  </section>

                  <section className="rd-form-section">
                    <div className="rd-form-section-head">
                      <div className="rd-form-step">05</div>
                      <div>
                        <h3 className="rd-h3">Evaluation Context</h3>
                        <p className="rd-muted">
                          The more precise the context, the more tailored the documentation package can be.
                        </p>
                      </div>
                    </div>

                    <div className="rd-form-grid rd-form-grid--1">
                      <label className="rd-field">
                        <span className="rd-label">Primary use case</span>
                        <textarea
                          className="rd-input rd-textarea"
                          value={form.useCase}
                          onChange={(e) => handleChange("useCase", e.target.value)}
                          placeholder="Tell us what you're evaluating: new launch, replacing an existing provider, white-label expansion, sweepstakes model, investor diligence, acquisition, etc."
                          required
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Current stack / provider situation</span>
                        <textarea
                          className="rd-input rd-textarea rd-textarea--sm"
                          value={form.currentStack}
                          onChange={(e) => handleChange("currentStack", e.target.value)}
                          placeholder="Optional: what are you using now, what is missing, and what are you trying to improve?"
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Integration requirements</span>
                        <textarea
                          className="rd-input rd-textarea rd-textarea--sm"
                          value={form.integrationNeeds}
                          onChange={(e) => handleChange("integrationNeeds", e.target.value)}
                          placeholder="Wallets, payments, crypto, custom games, admin controls, reporting, white-label branding, affiliate systems, etc."
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Security / audit expectations</span>
                        <textarea
                          className="rd-input rd-textarea rd-textarea--sm"
                          value={form.securityExpectations}
                          onChange={(e) => handleChange("securityExpectations", e.target.value)}
                          placeholder="What level of transparency, auditability, evidence, or technical review are you expecting?"
                        />
                      </label>

                      <label className="rd-field">
                        <span className="rd-label">Additional notes</span>
                        <textarea
                          className="rd-input rd-textarea rd-textarea--sm"
                          value={form.additionalNotes}
                          onChange={(e) => handleChange("additionalNotes", e.target.value)}
                          placeholder="Anything else you'd like included in the documentation package or reviewed during follow-up."
                        />
                      </label>
                    </div>
                  </section>

                  <section className="rd-form-section">
                    <div className="rd-form-section-head">
                      <div className="rd-form-step">06</div>
                      <div>
                        <h3 className="rd-h3">Access Preferences</h3>
                        <p className="rd-muted">
                          Choose how you want the documentation review process handled.
                        </p>
                      </div>
                    </div>

                    <div className="rd-toggle-list">
                      <label className="rd-toggle-card">
                        <input
                          type="checkbox"
                          checked={form.ndaRequired}
                          onChange={(e) => handleChange("ndaRequired", e.target.checked)}
                        />
                        <span className="rd-toggle-ui" aria-hidden="true" />
                        <div>
                          <div className="rd-toggle-title">NDA preferred before technical review</div>
                          <div className="rd-toggle-desc">
                            Indicate whether controlled materials should only be shared after an NDA process.
                          </div>
                        </div>
                      </label>

                      <label className="rd-toggle-card">
                        <input
                          type="checkbox"
                          checked={form.wantsDemo}
                          onChange={(e) => handleChange("wantsDemo", e.target.checked)}
                        />
                        <span className="rd-toggle-ui" aria-hidden="true" />
                        <div>
                          <div className="rd-toggle-title">Pair docs with a live demo walkthrough</div>
                          <div className="rd-toggle-desc">
                            Recommended for operators evaluating launch-readiness, architecture, and admin flow.
                          </div>
                        </div>
                      </label>

                      <label className="rd-toggle-card">
                        <input
                          type="checkbox"
                          checked={form.agreesReview}
                          onChange={(e) => handleChange("agreesReview", e.target.checked)}
                          required
                        />
                        <span className="rd-toggle-ui" aria-hidden="true" />
                        <div>
                          <div className="rd-toggle-title">I understand requests are reviewed before access is granted</div>
                          <div className="rd-toggle-desc">
                            Public overviews remain available, while deeper technical materials are shared selectively.
                          </div>
                        </div>
                      </label>
                    </div>
                  </section>

                  <section className="rd-submit-panel">
                    <div className="rd-submit-copy">
                      <div className="rd-kicker">FINAL REVIEW</div>
                      <h3 className="rd-submit-title">Request official documentation access</h3>
                      <p className="rd-submit-text">
                        Qualified requests may receive architecture summaries, platform overview materials,
                        operator lifecycle docs, technical review bundles, or follow-up scheduling for a guided walkthrough.
                      </p>

                      <div className="rd-submit-pills">
                        <span className="rd-pill">Selective access</span>
                        <span className="rd-pill">Operator-focused</span>
                        <span className="rd-pill">Technical + commercial review</span>
                      </div>
                    </div>

                    <div className="rd-submit-actions">
                      <button className="rd-btn rd-btn--primary" type="submit">
                        Submit Request <span aria-hidden="true">→</span>
                      </button>
                      <a className="rd-btn rd-btn--ghost" href="/book-demo">
                        Book Live Demo
                      </a>
                    </div>

                    {submitted ? (
                      <div className="rd-success">
                        <div className="rd-success-title">Request submitted</div>
                        <div className="rd-success-body">
                          Your documentation request has been captured. A qualified follow-up can now review
                          fit, requested material categories, and whether a guided technical walkthrough makes sense.
                        </div>
                      </div>
                    ) : null}
                  </section>
                </form>
              </div>

              <aside className="rd-sidebar">
                <div className="rd-panel">
                  <div className="rd-panel-head">
                    <h3 className="rd-h3">What can be shared</h3>
                    <p className="rd-muted">
                      Materials vary depending on operator fit, timing, and review context.
                    </p>
                  </div>

                  <div className="rd-stack-list">
                    <div className="rd-stack-item">
                      <div className="rd-stack-title">Platform overview materials</div>
                      <div className="rd-stack-desc">
                        Positioning, operator use cases, commercial framing, and system capability summaries.
                      </div>
                    </div>
                    <div className="rd-stack-item">
                      <div className="rd-stack-title">Architecture & system design docs</div>
                      <div className="rd-stack-desc">
                        Multi-tenant structure, deployment posture, system boundaries, and infrastructure reasoning.
                      </div>
                    </div>
                    <div className="rd-stack-item">
                      <div className="rd-stack-title">Provably fair documentation</div>
                      <div className="rd-stack-desc">
                        Verification concepts, review flows, and fairness system explanation materials.
                      </div>
                    </div>
                    <div className="rd-stack-item">
                      <div className="rd-stack-title">Operator lifecycle docs</div>
                      <div className="rd-stack-desc">
                        Tenant creation, catalog management, branding, controls, and launch readiness flows.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rd-panel">
                  <div className="rd-panel-head">
                    <h3 className="rd-h3">Why access is controlled</h3>
                  </div>

                  <ul className="rd-bullet-list">
                    <li>Protects sensitive implementation detail while preserving trust</li>
                    <li>Keeps technical review aligned with real operator conversations</li>
                    <li>Allows materials to be tailored to launch, diligence, or acquisition use cases</li>
                    <li>Prevents public documentation from becoming a raw system map</li>
                  </ul>
                </div>

                <div style={{ paddingLeft: 10 }} className="rd-panel rd-panel--highlight">
                  <div className="rd-panel-head">
                    <div className="rd-small-kicker">FASTEST PATH</div>
                    <h3 className="rd-h3">Want both docs and context?</h3>
                  </div>
                  <p className="rd-muted">
                    The strongest evaluation flow is usually:
                  </p>
                  <ol className="rd-number-list">
                    <li>Submit request</li>
                    <li>Share launch or evaluation context</li>
                    <li>Receive tailored documentation set</li>
                    <li>Walk through live demo together</li>
                  </ol>
                  <a className="rd-inline-link" href="/book-demo">
                    Schedule walkthrough <span aria-hidden="true">→</span>
                  </a>
                </div>

                <div className="rd-panel">
                  <div className="rd-panel-head">
                    <h3 className="rd-h3">Ideal request examples</h3>
                  </div>
                  <div className="rd-example-card">
                    <div className="rd-example-label">Operator launch</div>
                    <div className="rd-example-text">
                      “We’re evaluating infrastructure for a branded launch in the next 60 days and
                      want architecture, fairness, and admin/control materials.”
                    </div>
                  </div>
                  <div className="rd-example-card">
                    <div className="rd-example-label">Replacement evaluation</div>
                    <div className="rd-example-text">
                      “We already operate and want to compare multi-tenant control, replay tooling,
                      and deployment flexibility against our current provider.”
                    </div>
                  </div>
                  <div className="rd-example-card">
                    <div className="rd-example-label">Acquisition / diligence</div>
                    <div className="rd-example-text">
                      “We are reviewing the platform from an investor or acquisition perspective and
                      want official materials supporting technical credibility.”
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </Fragment>
  );
};

export default RequestDocumentation;